import { Component, Input, OnInit } from '@angular/core';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { ChipContent } from 'src/app/shared/models/chip-content/chip-content';
import { FilterType } from 'src/app/shared/models/constants/filter-type';
import { PointSummaryDTO } from 'src/app/shared/models/dtos/responses/point-summary/point-summary-dto';
import { BusinessResponseDTO } from 'src/app/shared/models/dtos/register-form/business-response-dto/business-response-dto';
import { FormControl, FormGroup } from '@angular/forms';
import { ProductService } from 'src/app/core/api/product/product.service';
import { FamilyDTO } from 'src/app/shared/models/dtos/responses/family/family';
import {
  generateSeriesChips,
  MultiSelectChip,
} from '../filter-container/filter-container.component';
import { FilterOptions } from 'src/app/shared/models/filters/filter-options';
import { RewardManagementService } from 'src/app/core/services/reward-management/reward-management.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-point-balance',
  templateUrl: './point-balance.component.html',
  styleUrls: ['./point-balance.component.scss'],
})

/**
 * Display main components of point balance page
 */
export class PointBalanceComponent implements OnInit {
  @Input() business?: BusinessResponseDTO;
  @Input() isMobile = true;

  familySelectOptions: string[] = [];

  familyFormGroup = new FormGroup({
    family: new FormControl('', []),
  });

  initialFamilyId?: number;

  seriesChips: MultiSelectChip[] = [];

  seriesFilter: FilterOptions = {
    series: [],
  };

  pointSummary?: PointSummaryDTO;

  private pointsAddedChip = new ChipContent(
    'stars',
    'points-added',
    FilterType.POINTS_ADDED,
    false
  );
  private pointsRedeemedChip = new ChipContent(
    'confirmation_number',
    'points-redeemed',
    FilterType.POINTS_REDEEMED,
    false
  );
  private pointsExpiredChip = new ChipContent(
    'event_busy',
    'points-expired',
    FilterType.POINTS_EXPIRED,
    false
  );
  chipContents: ChipContent[] = [
    this.pointsAddedChip,
    this.pointsRedeemedChip,
    this.pointsExpiredChip,
  ];

  filters = {
    added: false,
    spend: false,
    expired: false,
    removed: false,
    startDate: undefined,
    endDate: undefined,
  };

  labels = {
    pointBalanceLabel: 'point-balance',
    generateReport: 'generate-report',
    selectFamily: 'select-family',
    filterLabel: 'filter-label',
    clearAllLabel: 'clear-all',
    pointsTimelineLabel: 'points-timeline',
  };

  /**
   * @constructor
   */
  constructor(
    private readonly translationService: TranslationService,
    public readonly rewardManagementService: RewardManagementService,
    private productService: ProductService,
    private route: ActivatedRoute
  ) {}

  /**
   * ngOnInit()
   */
  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.initialFamilyId = Number(params['familyId']);
    });

    this.translationService.translateChipContentArray(this.chipContents);
    this.translationService.translateObject(this.labels);

    this.getPrintersFamily();
  }

  /**
   * Captures all the families from registered equipment
   */
  getPrintersFamily() {
    this.productService.getBusinessEquipments().subscribe((equipments) => {
      let allFamilies = equipments
        .map((e) => e.family)
        .filter((f): f is FamilyDTO => !!f);
      allFamilies = Array.from(
        new Map(allFamilies.map((f) => [f.id, f])).values()
      );

      this.seriesChips = generateSeriesChips(allFamilies);
      if (this.seriesChips.length > 0) {
        let selectedIndex = 0;
        if (this.initialFamilyId && Array.isArray(this.seriesChips)) {
          const foundIndex = this.seriesChips.findIndex(
            (chip) => chip.id === this.initialFamilyId
          );
          if (foundIndex !== -1) {
            selectedIndex = foundIndex;
          }
        }

        this.seriesChips[selectedIndex].isActive = true;
        const initialSeriesFilter: FilterOptions = {
          series: [this.seriesChips[selectedIndex].id],
        };
        this.updateFilter(initialSeriesFilter);
      }
    });
  }

  /**
   * Updates Filters
   * @param {FilterOptions} filters
   */
  updateFilter(filters: FilterOptions) {
    this.filters = { ...this.filters, ...filters };

    this.seriesFilter = {
      series: filters.series,
    };
    if (filters.series && filters.series.length > 0) {
      this.rewardManagementService
        .pullPoints(parseInt(this.seriesFilter.series![0]))
        .then((result) => {
          this.pointSummary = result;
        });
    }
  }

  /**
   * A shortcut for family form control
   */
  get familyFormControl() {
    return this.familyFormGroup.controls['family'];
  }

  /**
   * Generate filter params and emit it
   * @param {ChipContent} element
   */
  async getFilter(element: ChipContent) {
    if (element.filterType === FilterType.POINTS_ADDED) {
      element.isActive = !element.isActive;
      this.filters.added = !this.filters.added;
    }

    if (element.filterType === FilterType.POINTS_REDEEMED) {
      element.isActive = !element.isActive;
      this.filters.spend = !this.filters.spend;
      this.filters.removed = !this.filters.removed;
    }

    if (element.filterType === FilterType.POINTS_EXPIRED) {
      element.isActive = !element.isActive;
      this.filters.expired = !this.filters.expired;
    }

    this.filters = { ...this.filters };
  }

  /**
   * Generate filter params and emit it
   * @param {any} filters
   */
  async setDateRangeFilter(filters: any) {
    this.filters.endDate = filters.endDate;
    this.filters.startDate = filters.startDate;
    this.filters = { ...this.filters };
  }

  /**
   * Checks if any chip filter is currently active
   * @return {boolean}
   */
  hasActiveFilters(): boolean {
    return this.chipContents.some((chip) => chip.isActive);
  }

  /**
   * Clears all chip filters, keeping the date range filter untouched
   */
  clearAllFilters() {
    this.chipContents.forEach((chip) => (chip.isActive = false));
    this.filters.added = false;
    this.filters.spend = false;
    this.filters.removed = false;
    this.filters.expired = false;
    this.filters = { ...this.filters };
  }
}
